import { useLazyQuery } from "@apollo/client";
import { Alert, Collapse, DialogContentText } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useBulkDeleteSubmissions } from "hooks/useSubmissionMutations";
import pluralize from "pluralize";
import { SUBMISSION_REVISION_CHECK_QUERY } from "queries/submissions";
import { useEffect, useMemo } from "react";

import { Row } from "./types";
import { partitionSubmissions } from "./utils";

type DeleteSubmissionsConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function DeleteSubmissionsConfirmationDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: DeleteSubmissionsConfirmationDialogProps) {
  const alerts = useAlerts();
  const [bulkDeleteSubmissions, { loading: deleteLoading }] = useBulkDeleteSubmissions();
  const [checkRevisions, { data: revisionData, loading: revisionCheckLoading }] = useLazyQuery(
    SUBMISSION_REVISION_CHECK_QUERY
  );

  const { virtualEventIds, uniqueSubmissionIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );

  useEffect(() => {
    if (open && uniqueSubmissionIds.length > 0) {
      checkRevisions({
        variables: { submissionIds: uniqueSubmissionIds },
      });
    }
  }, [open, uniqueSubmissionIds, checkRevisions]);

  const submissionsWithRevisions = useMemo(
    () =>
      revisionData?.getRevisionSubmissions?.filter((s) => s.revisionSubmissions.length > 0) ?? [],
    [revisionData]
  );

  const hasRevisions = submissionsWithRevisions.length > 0;

  const handleConfirm = async () => {
    try {
      await bulkDeleteSubmissions({
        variables: {
          input: {
            submissionIds: uniqueSubmissionIds,
            virtualEventIds: virtualEventIds,
          },
        },
      });

      alerts.success("Submission deleted successfully");
      onSuccess();
      onClose();
    } catch (error) {
      alerts.error("Failed to delete submission", error);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      loading={deleteLoading}
      onClose={onClose}
      header="Delete Submissions"
      disableConfirm={hasRevisions || revisionCheckLoading}
      onConfirm={handleConfirm}
    >
      <DialogContentText>
        {`Are you sure you want to delete the selected ${
          submissions.length > 1 ? submissions.length : ""
        } ${pluralize(
          "submission",
          submissions.length
        )}? This will only affect these instances of submissions and will not affect any recurring requirements. Any submission documents will also be deleted.`}
      </DialogContentText>
      <Collapse in={hasRevisions} sx={{ mt: 2 }}>
        <Alert severity="error">
          {submissionsWithRevisions.length === 1
            ? `"${submissionsWithRevisions[0].title ?? "Untitled Submission"}" has linked revision submissions and cannot be deleted. Please delete the revision submissions first.`
            : `${submissionsWithRevisions.length} of the selected submissions have linked revision submissions and cannot be deleted. Please delete the revision submissions first.`}
        </Alert>
      </Collapse>
    </ConfirmDialog>
  );
}
